// assets/gift-options.js
(() => {
  if (window.__HK_GIFT_OPTIONS_INIT__) return;
  window.__HK_GIFT_OPTIONS_INIT__ = true;

  const SELECTORS = {
    root: "[data-hk-gift]",
    toggle: ".gift-opt__toggle",       // Send as a gift (adds bag)
    details: ".gift-opt__details",     // Wrap + message panel
    wrap: ".gift-opt__wrap",           // Gift wrapping (free)
    textarea: ".gift-opt__textarea",   // Gift message
    count: ".gift-opt__count",
    status: ".gift-opt__status",

    // NEW: invoice option (attribute only)
    invoice: ".invoice-opt__toggle",
  };

  const ATTR_KEYS = {
    isGift: "gift_is_gift",
    wrap: "gift_wrap",
    msg: "gift_message",
    invoiceRemove: "invoice_remove",
  };

  // -----------------------------
  // Utils
  // -----------------------------
  function setStatus(root, text, type = "info") {
    const el = root?.querySelector?.(SELECTORS.status);
    if (!el) return;
    el.textContent = text || "";
    el.style.display = text ? "block" : "none";
    el.style.color = type === "error" ? "var(--color-error, #b00020)" : "";
  }

  async function fetchJson(url, opts) {
    const res = await fetch(url, opts);
    let json = null;
    try { json = await res.json(); } catch (e) {}
    if (!res.ok) {
      const msg =
        (json && (json.description || json.message)) ||
        (typeof json === "string" ? json : null) ||
        `Request failed (${res.status})`;
      throw new Error(msg);
    }
    return json;
  }

  async function cartJson() {
    return fetchJson("/cart.js", { credentials: "same-origin" });
  }

  async function updateAttributes(attrs) {
    return fetchJson("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ attributes: attrs }),
    });
  }

  async function addVariant(variantId, qty) {
    return fetchJson("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: Number(variantId), quantity: Number(qty || 1) }),
    });
  }

  // Shopify supports change.js with:
  // - { line: N, quantity: Q }
  // - OR { id: "line_item_key", quantity: Q }  (more reliable)
  async function changeLine(payload) {
    return fetchJson("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    });
  }

  function findVariantLine(cart, variantId) {
    if (!cart?.items?.length) return null;

    const idx = cart.items.findIndex(
      (it) => Number(it.id) === Number(variantId) || Number(it.variant_id) === Number(variantId)
    );

    if (idx === -1) return null;

    const item = cart.items[idx];
    return {
      line: idx + 1,
      key: item?.key || null,
      item,
    };
  }

  function updateCharCount(root) {
    const ta = root.querySelector(SELECTORS.textarea);
    const counter = root.querySelector(SELECTORS.count);
    if (!ta || !counter) return;
    const max = Number(ta.getAttribute("maxlength") || 250);
    counter.textContent = `${ta.value.length} / ${max}`;
  }

  function showHideDetails(root, show) {
    const details = root.querySelector(SELECTORS.details);
    if (!details) return;
    details.style.display = show ? "" : "none";
  }

  // -----------------------------
  // Prestige cart drawer refresh
  // -----------------------------
  function prestigeRefreshDrawer() {
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  function waitForDrawerRefreshed(timeoutMs = 2500) {
    return new Promise((resolve) => {
      let done = false;

      const t = setTimeout(() => {
        if (done) return;
        done = true;
        document.removeEventListener("cart-drawer:refreshed", onRefreshed, true);
        resolve(false);
      }, timeoutMs);

      function onRefreshed() {
        if (done) return;
        done = true;
        clearTimeout(t);
        document.removeEventListener("cart-drawer:refreshed", onRefreshed, true);
        resolve(true);
      }

      document.addEventListener("cart-drawer:refreshed", onRefreshed, true);
    });
  }

  // NOTE: We do NOT call sync here to avoid “double sync” loops.
  async function refreshDrawerOnly() {
    markGiftPanelForRestore();
    prestigeRefreshDrawer();
    await waitForDrawerRefreshed(2500);
  }

  function markGiftPanelForRestore() {
    const drawer = document.querySelector("#cart-drawer.cart-drawer--redesign");
    if (!drawer) return;

    const accordion = drawer.querySelector("#hk-gift-options");
    const body = drawer.shadowRoot?.querySelector('[part="body"]');
    const giftToggle = drawer.querySelector(".gift-opt__toggle");

    window.__hkGiftPanelRestore = {
      keepOpen: Boolean(accordion?.open || giftToggle?.checked),
      scrollTop: body?.scrollTop || 0,
    };
  }

  // -----------------------------
  // Sync UI state from cart
  // -----------------------------
  let syncing = false;
  async function syncGiftUIFromCart(scope = document) {
    if (syncing) return;
    syncing = true;

    try {
      const cart = await cartJson();
      const attrs = cart?.attributes || {};

      scope.querySelectorAll(SELECTORS.root).forEach((root) => {
        const toggle = root.querySelector(SELECTORS.toggle);
        const wrap = root.querySelector(SELECTORS.wrap);
        const ta = root.querySelector(SELECTORS.textarea);
        const inv = root.querySelector(SELECTORS.invoice);

        const bagVariantId = toggle?.dataset?.giftBagVariantId;
        const hasBag = bagVariantId ? !!findVariantLine(cart, bagVariantId) : false;

        // If bag exists => toggle should be on (even if attribute missing)
        const isGift = (attrs[ATTR_KEYS.isGift] || "") === "true" || hasBag;
        const isWrap = (attrs[ATTR_KEYS.wrap] || "") === "true";
        const msg = (attrs[ATTR_KEYS.msg] || "");
        const invoiceRemove = (attrs[ATTR_KEYS.invoiceRemove] || "") === "true";

        if (toggle) toggle.checked = !!isGift;
        if (wrap) wrap.checked = !!isWrap;
        if (inv) inv.checked = !!invoiceRemove;

        // Only set textarea value if user isn't typing
        if (ta && document.activeElement !== ta) {
          ta.value = msg;
        }

        showHideDetails(root, !!isGift);
        updateCharCount(root);
        setStatus(root, "");
      });

      return cart;
    } finally {
      syncing = false;
    }
  }

  // -----------------------------
  // Attribute saving (no drawer refresh)
  // -----------------------------
  let attrSaveTimer = null;
  let savingAttrs = false;

  function collectAttributesFromRoot(root) {
    const toggle = root.querySelector(SELECTORS.toggle);
    const wrap = root.querySelector(SELECTORS.wrap);
    const ta = root.querySelector(SELECTORS.textarea);
    const inv = root.querySelector(SELECTORS.invoice);

    return {
      [ATTR_KEYS.isGift]: toggle?.checked ? "true" : "",
      [ATTR_KEYS.wrap]: wrap?.checked ? "true" : "",
      [ATTR_KEYS.msg]: (ta?.value || "").trim(),
      [ATTR_KEYS.invoiceRemove]: inv?.checked ? "true" : "",
    };
  }

  async function saveAttributesFromRoot(root, { quiet = true } = {}) {
    if (!root || savingAttrs) return;
    savingAttrs = true;

    try {
      const attrs = collectAttributesFromRoot(root);

      if (!quiet) setStatus(root, "Saving…");
      await updateAttributes(attrs);
      if (!quiet) setStatus(root, "Saved.");
      // DO NOT refresh drawer for attributes (prevents “typing refresh” feel)
    } catch (err) {
      setStatus(root, `Save failed: ${err.message}`, "error");
    } finally {
      savingAttrs = false;
    }
  }

  function scheduleAttributeSave(root, delayMs = 900) {
    clearTimeout(attrSaveTimer);
    attrSaveTimer = setTimeout(() => {
      saveAttributesFromRoot(root, { quiet: true });
    }, delayMs);
  }

  // -----------------------------
  // Gift bag add/remove (refresh drawer after)
  // -----------------------------
  let bagOpLock = false;

  async function ensureGiftBag(variantId, shouldHave, root) {
    if (bagOpLock) return;
    bagOpLock = true;

    try {
      const cart1 = await cartJson();
      const found1 = findVariantLine(cart1, variantId);

      if (shouldHave) {
        if (!found1) {
          setStatus(root, "Adding gift bag…");
          await addVariant(variantId, 1);
        }
      } else {
        if (found1) {
          setStatus(root, "Removing gift bag…");

          // Prefer line item key for reliability
          if (found1.key) {
            await changeLine({ id: found1.key, quantity: 0 });
          } else {
            await changeLine({ line: found1.line, quantity: 0 });
          }
        }
      }

      // Force Prestige cart-drawer to re-render immediately (without closing)
      await refreshDrawerOnly();

      setStatus(root, "");
    } catch (err) {
      setStatus(root, `Gift bag update failed: ${err.message}`, "error");
    } finally {
      bagOpLock = false;
    }
  }

  // -----------------------------
  // Event bindings (delegated)
  // -----------------------------
  document.addEventListener(
    "change",
    async (e) => {
      const root = e.target.closest?.(SELECTORS.root);
      if (!root) return;

      // Send as gift toggle
      if (e.target.matches(SELECTORS.toggle)) {
        const variantId = e.target.dataset.giftBagVariantId;

        // Show/hide details instantly
        showHideDetails(root, e.target.checked);

        if (!variantId) {
          setStatus(root, "Missing gift bag variant id.", "error");
          return;
        }

        if (e.target.checked) {
          // 1) Save attributes (no refresh)
          await saveAttributesFromRoot(root, { quiet: true });

          // 2) Add bag + refresh drawer
          await ensureGiftBag(variantId, true, root);
        } else {
          // Turning off: clear wrap + message (UI + attributes)
          const wrap = root.querySelector(SELECTORS.wrap);
          const ta = root.querySelector(SELECTORS.textarea);
          if (wrap) wrap.checked = false;
          if (ta) ta.value = "";
          updateCharCount(root);

          await saveAttributesFromRoot(root, { quiet: true });
          await ensureGiftBag(variantId, false, root);
        }

        return;
      }

      // Gift wrapping checkbox (attributes only)
      if (e.target.matches(SELECTORS.wrap)) {
        await saveAttributesFromRoot(root, { quiet: true });
        return;
      }

      // Invoice option checkbox (attributes only)
      if (e.target.matches(SELECTORS.invoice)) {
        await saveAttributesFromRoot(root, { quiet: true });
        return;
      }
    },
    true
  );

  // Message typing: no refresh, just debounce save
  document.addEventListener(
    "input",
    (e) => {
      const root = e.target.closest?.(SELECTORS.root);
      if (!root) return;
      if (e.target.matches(SELECTORS.textarea)) {
        updateCharCount(root);
        scheduleAttributeSave(root, 900);
      }
    },
    true
  );

  // On blur: save immediately
  document.addEventListener(
    "focusout",
    (e) => {
      const root = e.target.closest?.(SELECTORS.root);
      if (!root) return;
      if (e.target.matches(SELECTORS.textarea)) {
        clearTimeout(attrSaveTimer);
        saveAttributesFromRoot(root, { quiet: true });
      }
    },
    true
  );

  // When Prestige re-renders the drawer, re-sync states ONCE
  document.addEventListener(
    "cart-drawer:refreshed",
    () => {
      syncGiftUIFromCart(document).catch(() => {});
    },
    true
  );

  // When drawer is about to open, sync
  document.addEventListener(
    "dialog:before-show",
    (e) => {
      // In Prestige, this event fires on the drawer element
      const isDrawer = e.target && (e.target.tagName === "CART-DRAWER" || e.target.closest?.("cart-drawer"));
      if (!isDrawer) return;
      syncGiftUIFromCart(document).catch(() => {});
    },
    true
  );

  // Initial sync
  document.addEventListener("DOMContentLoaded", () => {
    syncGiftUIFromCart(document).catch(() => {});
  });
})();
